import React from 'react';
import { apiClient } from '../../../lib/apiClient';

export type ReusableCreativeStatus =
  | 'active'
  | 'pending_review'
  | 'voided'
  | 'retired'
  | 'withdrawn'
  | 'abandoned'
  | string;

export interface ReusableLibraryCampaignRef {
  submission_id: string;
  campaign_name: string;
  submission_status: string;
  creative_status: ReusableCreativeStatus;
  click_through_url: string | null;
  start_date: string | null;
  end_date: string | null;
  updated_at: string;
}

export interface ReusableLibraryItem {
  filename: string;
  size_bytes: number | null;
  mime_type: string | null;
  first_seen_at: string;
  preview_storage_path: string;
  campaigns: ReusableLibraryCampaignRef[];
}

// CTV-526: tile the user has hidden from the active library. Same shape as a
// ReusableLibraryItem plus archive audit columns. The wizard reads this list
// to detect upload-collision matches against `(filename, size_bytes)`.
export interface ArchivedLibraryItem extends ReusableLibraryItem {
  archived_at: string;
  archived_by: string;
}

// "Reusable" means the library item has at least one campaign whose creative
// is in a state worth re-running: active (live and approved), pending_review
// (uploaded but not yet decisioned), retired (was active, then swapped out —
// still a legitimate previous-version), withdrawn (approved but the buyer
// removed it before its window began — same prior-approval trust as retired),
// or voided (CTV-463: kept visible so the yellow "Previously rejected here"
// badge can render; picking still works, the submission just won't skip
// review). abandoned (CTV-611: buyer removed it before any decision) keeps
// the tile reusable too — the file exists and carries no verdict either way.
const REUSABLE_STATUSES = new Set<ReusableCreativeStatus>([
  'active',
  'pending_review',
  'retired',
  'withdrawn',
  'voided',
  'abandoned',
]);

function isReusable(item: ReusableLibraryItem): boolean {
  return item.campaigns.some((c) => REUSABLE_STATUSES.has(c.creative_status));
}

export type PriorApprovalBadge = 'approved' | 'rejected' | null;

/**
 * CTV-463 / ADR-0007 (revised 2026-06-08) frontend mirror of the server-side
 * predicate. Uses the most recent decisive verdict, skipping pending rows.
 * The Library API already filters by station (and advertiser seat) and sorts
 * `campaigns[]` newest-first by `updated_at`, so iterating in order gives us
 * the latest decision first.
 *
 * - first decisive verdict = 'active', 'retired', or 'withdrawn' → 'approved'
 * - first decisive verdict = 'voided' → 'rejected'
 * - no decisive verdicts (only pending) → null
 */
export function priorApprovalBadge(item: ReusableLibraryItem): PriorApprovalBadge {
  for (const c of item.campaigns) {
    if (
      c.creative_status === 'active' ||
      c.creative_status === 'retired' ||
      c.creative_status === 'withdrawn'
    ) {
      return 'approved';
    }
    if (c.creative_status === 'voided') return 'rejected';
    // pending_review (and anything else) is not a verdict — keep looking.
  }
  return null;
}

export function pickClickThroughForReuse(item: ReusableLibraryItem): string {
  // Pull the most-recent campaign's clickthrough — the API already returns
  // campaigns newest-first, so [0] is enough. Fall back to '' so the form
  // value remains a string rather than null.
  return item.campaigns[0]?.click_through_url ?? '';
}

/**
 * CTV-457: fetches the per-station, per-advertiser-seat library used by the
 * wizard's "Pick from library" picker (and the gate that decides whether the
 * button itself renders). Filters out items with no campaign in a reusable
 * status (see REUSABLE_STATUSES) so callers can treat the result as the set
 * of actually reusable commercials.
 */
export function useReusableLibrary(
  slug: string | undefined,
  advertiserId: string | null | undefined,
): {
  items: ReusableLibraryItem[];
  // CTV-526: archived tiles at this (station, advertiser). Surfaced so the
  // wizard's upload-collision prompt can match a freshly picked file's
  // (filename, size_bytes) against archived fingerprints without an extra
  // round trip. Library Picker Modal still reads only `items`.
  archived: ArchivedLibraryItem[];
  isLoading: boolean;
  error: string | null;
  hasReusable: boolean;
} {
  const [items, setItems] = React.useState<ReusableLibraryItem[]>([]);
  const [archived, setArchived] = React.useState<ArchivedLibraryItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!slug || !advertiserId) {
      setItems([]);
      setArchived([]);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<{ items: ReusableLibraryItem[]; archived?: ArchivedLibraryItem[] }>(
        `/creatives/library/${slug}?advertiserId=${encodeURIComponent(advertiserId)}`,
      )
      .then((res) => {
        if (cancelled) return;
        const all = res.items ?? [];
        setItems(all.filter(isReusable));
        setArchived(res.archived ?? []);
        setIsLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load library');
        setItems([]);
        setArchived([]);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, advertiserId]);

  return { items, archived, isLoading, error, hasReusable: items.length > 0 };
}
